// SPDX-License-Identifier: LicenseRef-Proprietary
// Copyright (c) 2026 Invariant Holdings LLC
// ── @csk/core theorem imports ────────────────────────────────
// These are the actual enforcement functions from the TypeScript realization.
import { allocate as coreResAlloc, consume as coreResConsume, managedAllocate as coreManagedAlloc, release as coreManagedRelease, establish as coreEstablish, preservingTransition, safeCompose, } from "@csk/core";
import { createCompileTimeContractFragments, createMetadataContract, } from "./compile-time-contracts.js";
import { createSeamBridgeKernelContract } from "./seam-bridge-kernel.js";
/**
 * Create a `WasmKernel` backed by `@csk/core`'s actual theorem enforcement.
 *
 * For RES.CONSUME, RES.RELEASE, INV.ESTABLISH, INV.PRESERVE, and COMPOSE,
 * this kernel delegates to `@csk/core`'s enforcement functions, which throw
 * on violations using the same logic as the TypeScript realization.
 *
 * For SEPARATE, DATA.ORIGIN, DATA.REACH, PERMIT, and PRECOMMIT — where
 * `@csk/core` enforcement is purely compile-time (type-level) — this
 * kernel uses the same string-encoding as the WASM bridge simulation.
 */
export function createCoreBackedKernel() {
    let nextId = 1;
    const allocId = () => nextId++;
    // ── RES.CONSUME: backed by @csk/core allocate/consume ──────
    const resources = new Map();
    // ── RES.RELEASE: backed by @csk/core managedAllocate/release
    const managedResources = new Map();
    // ── INV.ESTABLISH: backed by @csk/core establish ────────────
    const builders = new Map();
    const coreEstablishedStates = new Map();
    const stateMachines = new Map();
    // ── COMPOSE: backed by @csk/core safeCompose ────────────────
    const operations = new Map();
    const chains = new Map();
    const compileTimeContracts = createCompileTimeContractFragments(allocId);
    const metadataContract = createMetadataContract("0.1.0-core-backed");
    const seamBridgeContract = createSeamBridgeKernelContract();
    return {
        // ── RES.CONSUME ────────────────────────────────────────────
        resource_allocate(label) {
            const id = allocId();
            const handle = coreResAlloc(`resource-${String(id)}`, label);
            resources.set(id, handle);
            return id;
        },
        resource_consume(handle_id) {
            const handle = resources.get(handle_id);
            if (!handle) {
                throw new Error(`RES.CONSUME violation: handle ${String(handle_id)} never allocated`);
            }
            // coreResConsume throws on double-consume (same error as @csk/core)
            return coreResConsume(handle);
        },
        resource_is_live(handle_id) {
            const handle = resources.get(handle_id);
            if (!handle)
                return false;
            // Access brand-erased consumed flag — same ESCAPE as @csk/core resConsume.ts
            const h = handle; // ESCAPE: brand erasure to read consumed flag; no exported predicate from @csk/core; pattern mirrors @csk/core consume() impl
            return !h.consumed;
        },
        resource_live_count() {
            let count = 0;
            for (const handle of resources.values()) {
                const h = handle; // ESCAPE: brand erasure to read consumed flag; pattern mirrors @csk/core consume() impl
                if (!h.consumed)
                    count++;
            }
            return count;
        },
        // ── RES.RELEASE ────────────────────────────────────────────
        managed_create(label) {
            const id = allocId();
            const managed = coreManagedAlloc(`managed-${String(id)}`, label);
            managedResources.set(id, managed);
            return id;
        },
        managed_release(id) {
            const managed = managedResources.get(id);
            if (!managed)
                throw new Error(`RES.RELEASE: resource ${String(id)} not found`);
            // coreManagedRelease throws on double-release (same error as @csk/core)
            coreManagedRelease(managed);
        },
        managed_is_released(id) {
            const managed = managedResources.get(id);
            if (!managed)
                return false;
            // Access brand-erased released flag — same ESCAPE as @csk/core resRelease.ts
            const m = managed; // ESCAPE: brand erasure to read released flag; no exported predicate from @csk/core; pattern mirrors @csk/core release() impl
            return m.released;
        },
        managed_use_and_release(id) {
            const managed = managedResources.get(id);
            if (!managed)
                throw new Error(`RES.RELEASE: resource ${String(id)} not found`);
            const m = managed; // ESCAPE: brand erasure to check released flag before release; pattern mirrors @csk/core release() impl
            if (m.released)
                throw new Error(`RES.RELEASE: resource ${String(id)} already released`);
            coreManagedRelease(managed);
        },
        ...compileTimeContracts,
        // ── INV.ESTABLISH: backed by @csk/core establish ───────────
        inv_builder_create() {
            const id = allocId();
            builders.set(id, { checks: [], consumed: false });
            return id;
        },
        inv_builder_add_check(builder_id, check_name) {
            const builder = builders.get(builder_id);
            if (!builder)
                throw new Error(`INV.ESTABLISH: builder ${String(builder_id)} not found`);
            if (builder.consumed)
                throw new Error(`INV.ESTABLISH: builder ${String(builder_id)} already consumed`);
            builder.checks.push(check_name);
        },
        inv_establish(builder_id, state_name) {
            const builder = builders.get(builder_id);
            if (!builder)
                throw new Error(`INV.ESTABLISH: builder ${String(builder_id)} not found`);
            if (builder.consumed)
                throw new Error(`INV.ESTABLISH: builder ${String(builder_id)} already consumed`);
            builder.consumed = true;
            // Delegate to @csk/core establish() — throws if any invariant fails
            const invariants = builder.checks.map((checkName) => ({
                name: checkName,
                // Each check verifies the state_name is non-empty (structural establishment)
                check: (s) => s.length > 0,
            }));
            const established = coreEstablish(state_name, invariants);
            const id = allocId();
            coreEstablishedStates.set(id, { state: established, name: state_name });
            return id;
        },
        inv_state_name(state_id) {
            const record = coreEstablishedStates.get(state_id);
            if (!record)
                throw new Error(`INV.ESTABLISH: state ${String(state_id)} not found`);
            return record.name;
        },
        // ── INV.PRESERVE: backed by @csk/core preservingTransition ─
        inv_machine_create(initial_state) {
            const id = allocId();
            // Establish initial state using @csk/core establish()
            const established = coreEstablish(initial_state, [
                { name: "non-empty-state", check: (s) => s.length > 0 },
            ]);
            stateMachines.set(id, {
                current_state: established,
                current_label: initial_state,
                allowed: [],
                transition_count: 0,
            });
            return id;
        },
        inv_machine_allow_transition(machine_id, from_state, to_state) {
            const machine = stateMachines.get(machine_id);
            if (!machine)
                throw new Error(`INV.PRESERVE: machine ${String(machine_id)} not found`);
            machine.allowed.push({ from: from_state, to: to_state });
        },
        inv_machine_transition(machine_id, new_state) {
            const machine = stateMachines.get(machine_id);
            if (!machine)
                throw new Error(`INV.PRESERVE: machine ${String(machine_id)} not found`);
            const currentLabel = machine.current_label;
            const isAllowed = machine.allowed.some((t) => t.from === currentLabel && t.to === new_state);
            if (!isAllowed) {
                throw new Error(`INV.PRESERVE: transition ${currentLabel} → ${new_state} not allowed`);
            }
            // Delegate to @csk/core preservingTransition() — throws if invariant broken
            const invariants = [
                { name: "non-empty-state", check: (s) => s.length > 0 },
                {
                    name: "allowed-transition",
                    check: (s) => machine.allowed.some((t) => t.from === currentLabel && t.to === s),
                },
            ];
            const nextEstablished = preservingTransition(machine.current_state, {
                name: `${currentLabel}→${new_state}`,
                transition: (_s) => new_state,
            }, invariants);
            machine.current_state = nextEstablished;
            machine.current_label = new_state;
            machine.transition_count++;
        },
        inv_machine_current_state(machine_id) {
            const machine = stateMachines.get(machine_id);
            if (!machine)
                throw new Error(`INV.PRESERVE: machine ${String(machine_id)} not found`);
            return machine.current_label;
        },
        inv_machine_transition_count(machine_id) {
            const machine = stateMachines.get(machine_id);
            if (!machine)
                throw new Error(`INV.PRESERVE: machine ${String(machine_id)} not found`);
            return machine.transition_count;
        },
        // ── COMPOSE: backed by @csk/core safeCompose ────────────────
        compose_register_operation(name, input_type, output_type) {
            const n = name.trim();
            const it = input_type.trim();
            const ot = output_type.trim();
            if (n.length === 0)
                throw new Error("COMPOSE: operation name must not be empty");
            if (it.length === 0)
                throw new Error("COMPOSE: input_type must not be empty");
            if (ot.length === 0)
                throw new Error("COMPOSE: output_type must not be empty");
            const id = allocId();
            // Create a @csk/core TypedOperation (apply is identity — type structure is the proof)
            const coreOp = {
                inputType: it,
                outputType: ot,
                apply: (x) => x,
            };
            operations.set(id, { name: n, input_type: it, output_type: ot, coreOp });
            return id;
        },
        compose_chain(first_id, second_id) {
            const firstEntry = operations.get(first_id) ??
                (() => {
                    const c = chains.get(first_id);
                    if (!c)
                        return undefined;
                    return {
                        name: `chain-${String(first_id)}`,
                        input_type: c.input_type,
                        output_type: c.output_type,
                        coreOp: c.coreChain,
                    };
                })();
            const secondEntry = operations.get(second_id) ??
                (() => {
                    const c = chains.get(second_id);
                    if (!c)
                        return undefined;
                    return {
                        name: `chain-${String(second_id)}`,
                        input_type: c.input_type,
                        output_type: c.output_type,
                        coreOp: c.coreChain,
                    };
                })();
            if (!firstEntry)
                throw new Error(`COMPOSE: operation ${String(first_id)} not found`);
            if (!secondEntry)
                throw new Error(`COMPOSE: operation ${String(second_id)} not found`);
            // Delegate type mismatch check to @csk/core safeCompose
            if (firstEntry.output_type !== secondEntry.input_type) {
                throw new Error(`COMPOSE: type mismatch — ${String(first_id)} outputs '${firstEntry.output_type}' but ${String(second_id)} expects '${secondEntry.input_type}'`);
            }
            const composed = safeCompose(firstEntry.coreOp, secondEntry.coreOp);
            const ops = [];
            const firstChain = chains.get(first_id);
            if (firstChain) {
                ops.push(...firstChain.operations);
            }
            else {
                ops.push(first_id);
            }
            const secondChain = chains.get(second_id);
            if (secondChain) {
                ops.push(...secondChain.operations);
            }
            else {
                ops.push(second_id);
            }
            const id = allocId();
            chains.set(id, {
                operations: ops,
                input_type: firstEntry.input_type,
                output_type: secondEntry.output_type,
                coreChain: composed,
            });
            return id;
        },
        compose_validate_chain(chain_id) {
            const chain = chains.get(chain_id);
            if (chain) {
                const names = chain.operations
                    .map((id) => operations.get(id)?.name ?? `op:${String(id)}`)
                    .join(" → ");
                return `${chain.input_type} → ${chain.output_type} (stages: ${names})`;
            }
            const op = operations.get(chain_id);
            if (op) {
                return `${op.input_type} → ${op.output_type} (single: ${op.name})`;
            }
            throw new Error(`COMPOSE: chain or operation ${String(chain_id)} not found`);
        },
        ...metadataContract,
        ...seamBridgeContract,
    };
}
//# sourceMappingURL=core-backed.js.map